import api from './api';

export interface GstRate {
  id: string;
  name: string;
  description: string | null;
  // Total GST in percent
  rate: number;
  cgstRate: number;
  sgstRate: number;
  igstRate: number;
  // The rate a new line starts with
  isDefault: boolean;
  isActive: boolean;
  // Active HSN codes taxed at this rate
  hsnCodes?: number;
  createdAt: string;
  updatedAt: string;
}

export interface GstRateInput {
  rate: number;
  name?: string;
  description?: string | null;
  isActive?: boolean;
  isDefault?: boolean;
}

export interface GstRateQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
  sortBy?: 'rate' | 'name' | 'updatedAt';
  sortOrder?: 'ASC' | 'DESC';
}

export interface GstRatePage {
  data: GstRate[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface GstRateSummary {
  total: number;
  active: number;
  inactive: number;
  defaultRate: { id: string; name: string; rate: number } | null;
}

// Empty filters are left out of the request
const clean = (params: object) =>
  Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== ''));

export const gstRateService = {
  getPage: async (params: GstRateQueryParams): Promise<GstRatePage> => {
    const response = await api.get('/master-data/gst-rates', { params: clean(params) });
    return response.data;
  },

  getSummary: async (): Promise<GstRateSummary> => {
    const response = await api.get('/master-data/gst-rates/summary');
    return response.data;
  },

  // The rates offered on forms, lowest first
  getActive: async (): Promise<GstRate[]> => {
    const response = await api.get('/master-data/gst-rates/active');
    return response.data;
  },

  create: async (data: GstRateInput): Promise<GstRate> => {
    const response = await api.post('/master-data/gst-rates', data);
    return response.data;
  },

  update: async (id: string, data: Partial<GstRateInput>): Promise<GstRate> => {
    const response = await api.patch(`/master-data/gst-rates/${id}`, data);
    return response.data;
  },

  setDefault: async (id: string): Promise<GstRate> => {
    const response = await api.patch(`/master-data/gst-rates/${id}/set-default`);
    return response.data;
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`/master-data/gst-rates/${id}`);
  },
};
